import React, { useEffect, useRef, useState } from 'react';
import { Star, UploadCloud, X } from 'lucide-react';

import { completeProductImageUpload, prepareProductImageUpload, putProductImage } from './api';
import { tx } from './i18n';

// Picking product photos and handing them to the managed upload: the browser
// puts the original straight into storage, then XOmics checks it and makes its
// thumbnail, card and detail sizes (Products.js on the backend).
const ALLOWED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/avif']);
const MAX_IMAGE_BYTES = 20 * 1024 * 1024;
// A photo set per option value (a ring's nine finishes) besides the shared photos.
export const MAX_PRODUCT_IMAGES = 60;
// What a new product starts with, as the create form says.
export const NEW_PRODUCT_IMAGES = 12;

function formatBytes(bytes) {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

async function sha256File(file) {
  const digest = await window.crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

// optionValues ([{ option, value }]) links every uploaded photo to the choices
// it shows, such as the Gold finish; left out, the photos show for every choice.
// places (codes) are the spots on the storefront's pages the photos fill; left
// out, the photos are shown in no spot.
export async function uploadSelectedImages(productId, productName, images, onProgress, optionValues, places) {
  const ordered = [...images].sort((left, right) => Number(right.isMain) - Number(left.isMain));
  for (let index = 0; index < ordered.length; index += 1) {
    const selected = ordered[index];
    onProgress(tx("ui.products.preparing_image_value_of_length_779dcb50", { value: index + 1, length: ordered.length }));
    const declaration = {
      contentType: selected.file.type,
      byteSize: selected.file.size,
      sha256: await sha256File(selected.file),
    };
    const prepared = await prepareProductImageUpload(productId, declaration);
    if (!prepared.alreadyUploaded) {
      onProgress(tx("ui.products.uploading_image_value_of_length_df9cfc18", { value: index + 1, length: ordered.length }));
      await putProductImage(prepared.putUrl, selected.file);
    }
    onProgress(tx("ui.products.creating_image_sizes_value_of_length_6157fc11", { value: index + 1, length: ordered.length }));
    await completeProductImageUpload(productId, {
      ...declaration,
      objectKey: prepared.objectKey,
      altText: productName,
      isMain: selected.isMain,
      ...(optionValues ? { optionValues } : {}),
      ...(places ? { places } : {}),
    });
  }
}

export function useSelectedImages() {
  const [images, setImages] = useState([]);
  const current = useRef(images);
  useEffect(() => { current.current = images; }, [images]);
  useEffect(() => () => {
    current.current.forEach((image) => URL.revokeObjectURL(image.previewUrl));
  }, []);
  return [images, setImages];
}

// chooseMain: whether one of the picked photos becomes the main photo. A new
// product needs one; photos added to a product that has some leave its main alone.
export function ImagePicker({
  images, setImages, disabled, onError, compact = false, limit = MAX_PRODUCT_IMAGES, chooseMain = true,
}) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  function addFiles(fileList) {
    const incoming = Array.from(fileList ?? []);
    if (incoming.length === 0) return;
    const rejected = incoming.find((file) => (
      !ALLOWED_IMAGE_TYPES.has(file.type) || file.size < 32 || file.size > MAX_IMAGE_BYTES
    ));
    if (rejected) {
      onError(tx("ui.products.use_jpeg_png_webp_or_avif_images_up_to_20_mb_each_27e80cec"));
      return;
    }
    const unique = incoming.filter((file) => !images.some((image) => (
        image.file.name === file.name
        && image.file.size === file.size
        && image.file.lastModified === file.lastModified
    )));
    const available = limit - images.length;
    if (unique.length > available) onError(tx("ui.products.you_can_add_value_more_imagevalue2_615dfc2d", { value: Math.max(0, available), value2: available === 1 ? '' : 's' }));
    const accepted = unique.slice(0, Math.max(0, available));
    setImages([
        ...images,
        ...accepted.map((file, index) => ({
          id: `${file.name}-${file.size}-${file.lastModified}-${index}`,
          file,
          previewUrl: URL.createObjectURL(file),
          isMain: chooseMain && images.length === 0 && index === 0,
        })),
    ]);
    if (inputRef.current) inputRef.current.value = '';
  }

  function removeImage(id) {
    const removed = images.find((image) => image.id === id);
    if (removed) URL.revokeObjectURL(removed.previewUrl);
    const remaining = images.filter((image) => image.id !== id);
    if (chooseMain && removed?.isMain && remaining.length > 0) remaining[0] = { ...remaining[0], isMain: true };
    setImages(remaining);
  }

  return (
    <div className={`xo-product-image-picker${compact ? ' is-compact' : ''}${chooseMain ? '' : ' is-plain'}`}>
      <button type="button"
        className={`xo-product-dropzone${dragging ? ' is-dragging' : ''}`}
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) setDragging(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          addFiles(event.dataTransfer.files);
        }}>
        <UploadCloud aria-hidden="true" />
        <span><strong>{tx("ui.products.drop_product_images_here_9bcc78d4")}</strong><small>{tx("ui.products.or_choose_files_jpeg_png_webp_or_avif_6a3dbb43")}</small></span>
      </button>
      <input ref={inputRef} className="xo-visually-hidden" type="file" multiple
        accept="image/jpeg,image/png,image/webp,image/avif" disabled={disabled}
        onChange={(event) => addFiles(event.target.files)} />

      {images.length > 0 && (
        <div className="xo-product-image-selection">
          {images.map((image) => (
            <article key={image.id} className={`xo-product-image-choice${image.isMain ? ' is-main' : ''}`}>
              <img src={image.previewUrl} alt="" />
              <span>
                <strong>{image.file.name}</strong>
                <small>{formatBytes(image.file.size)}</small>
              </span>
              {chooseMain && (
                <button type="button" className="xo-product-image-main" disabled={disabled || image.isMain}
                  aria-label={tx("ui.products.use_name_as_the_main_image_dcee9d3e", { name: image.file.name })}
                  onClick={() => setImages(images.map((entry) => ({
                    ...entry, isMain: entry.id === image.id,
                  })))}>
                  <Star aria-hidden="true" />{image.isMain ? tx("ui.products.main_62bce942") : tx("ui.products.make_main_13c815c2")}
                </button>
              )}
              <button type="button" className="xo-icon-btn" disabled={disabled}
                aria-label={tx("ui.products.remove_name_df7193a5", { name: image.file.name })} onClick={() => removeImage(image.id)}>
                <X aria-hidden="true" />
              </button>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
